/**
 * AxisTree — Beleg-driven virtueller Tree (Track G, 2026-06-05).
 *
 * Architektur-Schnitt:
 * - Tree liest aus `/api/reports/tree` (echte line_items mit aufgelösten Achsen-Werten),
 *   NICHT aus Stammdaten. Nur belegte Knoten erscheinen. ALG2 taucht nur unter „Einnahmen"
 *   oder „Ausgaben" auf, wenn es dort tatsächlich Belege hat (löst das Doppel-Problem von
 *   Stammdaten-basiertem Tree).
 * - Cats ohne Belege erscheinen gar nicht im Tree — Stammdaten-Browser bleibt in den
 *   Einstellungen. Henne-Ei: neue Cats werden erst sichtbar, sobald ein Beleg sie nutzt.
 *
 * Klick = setFilter (useNavFilters) + navigate auf `targetPath`. Tree bleibt selbst
 * stabil (Counts werden aus allen Belegen berechnet, nicht aus dem gefilterten Subset).
 */
import { useMemo, useState, type FC, type ReactNode } from "react";
import { ChevronRightIcon, ChevronDownIcon } from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";
import clsx from "clsx";
import {
  useReportTree,
  useCategories,
  useAppSettings,
  DEFAULT_AXIS_ORDER,
  type AxisType,
  type TreeItem,
  type Stability,
} from "../lib/receipts";
import { gruppenRang } from "../../lib/gruppen.ts";
import { useNavFilters, type NavFilters } from "../lib/useNavFilters";

const DIRECTION_LABEL: Record<"expense" | "income" | "neutral", string> = {
  expense: "Ausgaben",
  income: "Einnahmen",
  neutral: "Neutral",
};

const STABILITY_LABEL: Record<"fixed" | "variable" | "none", string> = {
  fixed: "Fix",
  variable: "Variabel",
  none: "⚠️ ohne Kostenart-Standard",
};

/** Eine Tree-Achsen-Position für ein Item.  */
function getAxisValue(item: TreeItem, axis: AxisType): string | null {
  switch (axis) {
    case "gruppe":
      return item.gruppe;
    case "direction":
      return item.direction;
    case "stability":
      return item.stability ?? "none";
    case "category":
      return item.categoryId ?? "__none__";
    case "subcategory":
      return item.subcategoryId ?? null;
    case "subSubcategory":
      return item.subSubcategoryId ?? null;
    default:
      return null;
  }
}

type Lookups = {
  catName: Map<string, string>;
  subName: Map<string, string>;
  subSubName: Map<string, string>;
};

function labelForValue(axis: AxisType, value: string, lookups: Lookups): string {
  switch (axis) {
    case "gruppe":
      return value;
    case "direction":
      return DIRECTION_LABEL[value as "expense" | "income" | "neutral"] ?? value;
    case "stability":
      return STABILITY_LABEL[value as "fixed" | "variable" | "none"] ?? value;
    case "category":
      return value === "__none__" ? "Ohne Kategorie" : lookups.catName.get(value) ?? value;
    case "subcategory":
      return lookups.subName.get(value) ?? value;
    case "subSubcategory":
      return lookups.subSubName.get(value) ?? value;
    default:
      return value;
  }
}

function filterDeltaForValue(axis: AxisType, value: string): Partial<NavFilters> {
  switch (axis) {
    case "gruppe":
      return { gruppe: value };
    case "direction":
      // Tree zeigt 3 Buckets (expense/income/neutral), Filter unterstützt heute „expense|income|all" plus „neutral".
      return { direction: value as "expense" | "income" | "neutral" };
    case "stability":
      return { stability: value as Stability | "none" };
    case "category":
      return { categoryId: value === "__none__" ? undefined : value };
    case "subcategory":
      return { subcategoryId: value };
    case "subSubcategory":
      return { subSubcategoryId: value };
    default:
      return {};
  }
}

type TreeBucket = {
  key: string;
  axis: AxisType;
  value: string;
  label: string;
  itemCount: number;
  total: number;
  children: TreeBucket[];
};

/**
 * Rekursive Aggregation: für die Items dieses Knotens, baue die nächste Achsen-Ebene.
 * Returns Buckets der nächsten Achse + ihre Children (rekursiv).
 */
function buildBuckets(
  items: TreeItem[],
  axes: AxisType[],
  lookups: Lookups,
  parentPath: string,
): TreeBucket[] {
  if (axes.length === 0 || items.length === 0) return [];
  const [axis, ...rest] = axes;
  if (!axis) return [];
  const byValue = new Map<string, TreeItem[]>();
  for (const it of items) {
    const v = getAxisValue(it, axis);
    if (v === null) continue; // Position hat keinen Wert für diese Achse (z.B. SubSubcategory leer)
    const arr = byValue.get(v) ?? [];
    arr.push(it);
    byValue.set(v, arr);
  }
  const out: TreeBucket[] = [];
  for (const [value, group] of byValue) {
    const total = group.reduce((s, it) => s + it.effective, 0);
    const children = buildBuckets(group, rest, lookups, `${parentPath}|${axis}:${value}`);
    out.push({
      key: `${parentPath}|${axis}:${value}`,
      axis,
      value,
      label: labelForValue(axis, value, lookups),
      // S3: Positionen eindeutig zählen — eine aufgeteilte Position liefert mehrere Teile, ist aber EINE Position.
      itemCount: new Set(group.map((it) => it.lineItemId)).size,
      total,
      children,
    });
  }
  // Sortierung pro Achse: Knoten mit feststehender Reihenfolge erst, sonst alphabetisch nach Label.
  const orderFor = (axis: AxisType, value: string): number => {
    if (axis === "gruppe") return gruppenRang(value);
    if (axis === "direction") {
      const i = ["expense", "income", "neutral"].indexOf(value);
      return i === -1 ? 99 : i;
    }
    if (axis === "stability") {
      const i = ["variable", "fixed", "none"].indexOf(value);
      return i === -1 ? 99 : i;
    }
    return 99;
  };
  out.sort((a, b) => {
    const oa = orderFor(a.axis, a.value);
    const ob = orderFor(b.axis, b.value);
    if (oa !== ob) return oa - ob;
    return a.label.localeCompare(b.label, "de");
  });
  return out;
}

/** Filter-Pfad (cumulative Deltas) von der Wurzel bis zu einem Knoten — wird beim Klick gesetzt. */
function pathDeltaForBucket(bucket: TreeBucket, ancestors: TreeBucket[]): Partial<NavFilters> {
  const delta: Partial<NavFilters> = {};
  for (const a of ancestors) Object.assign(delta, filterDeltaForValue(a.axis, a.value));
  Object.assign(delta, filterDeltaForValue(bucket.axis, bucket.value));
  return delta;
}

type TreeNodeProps = {
  bucket: TreeBucket;
  ancestors: TreeBucket[];
  depth: number;
  onSelect: (delta: Partial<NavFilters>) => void;
  activeFilters: NavFilters;
};

const TreeNode: FC<TreeNodeProps> = ({ bucket, ancestors, depth, onSelect, activeFilters }) => {
  const hasChildren = bucket.children.length > 0;
  const [expanded, setExpanded] = useState<boolean>(depth === 0);

  // Aktiver Pfad: ALLE Filter-Werte vom Knoten und Eltern stimmen mit aktuellem Filter überein.
  const fullDelta = pathDeltaForBucket(bucket, ancestors);
  const isActive = Object.entries(fullDelta).every(
    ([k, v]) => (activeFilters as Record<string, unknown>)[k] === v,
  );

  return (
    <li>
      <div
        className={clsx(
          "flex items-center gap-1 py-1 px-2 rounded-ios text-[13px] cursor-pointer select-none transition",
          isActive ? "bg-ios-blue/10 text-ios-blue font-medium" : "text-ios-ink hover:bg-ios-line/40",
        )}
        style={{ paddingLeft: `${depth * 12 + 8}px` }}
      >
        {hasChildren ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setExpanded((v) => !v);
            }}
            className="p-0.5 -ml-0.5 text-ios-muted hover:text-ios-ink"
            aria-label={expanded ? "Zuklappen" : "Aufklappen"}
          >
            {expanded ? <ChevronDownIcon className="w-3.5 h-3.5" /> : <ChevronRightIcon className="w-3.5 h-3.5" />}
          </button>
        ) : (
          <span className="w-3.5 h-3.5 inline-block" />
        )}
        <button
          type="button"
          onClick={() => onSelect(fullDelta)}
          className="flex-1 text-left truncate"
          title={bucket.label}
        >
          {bucket.label}
        </button>
        <span className="text-[11px] text-ios-subtle tabular-nums shrink-0" title={`${bucket.itemCount} Position(en)`}>
          {bucket.itemCount}
        </span>
      </div>
      {expanded && hasChildren && (
        <ul className="space-y-0.5">
          {bucket.children.map((c) => (
            <TreeNode
              key={c.key}
              bucket={c}
              ancestors={[...ancestors, bucket]}
              depth={depth + 1}
              onSelect={onSelect}
              activeFilters={activeFilters}
            />
          ))}
        </ul>
      )}
    </li>
  );
};

type AxisTreeProps = {
  /** Ziel-Route beim Knoten-Klick (Default „/zuletzt"). */
  targetPath?: string;
  /** Optional: Container-Slot, der Sub-Children rendert (z.B. „Filter zurücksetzen"-Knopf). */
  children?: ReactNode;
};

export const AxisTree: FC<AxisTreeProps> = ({ targetPath = "/zuletzt", children }) => {
  const navigate = useNavigate();
  const { filters, patch, reset } = useNavFilters();
  const { data: tree, isLoading: treeLoading } = useReportTree({});
  const { data: cats } = useCategories();
  const { data: settings } = useAppSettings();

  const axisOrder: AxisType[] = settings?.axisOrder?.length ? settings.axisOrder : DEFAULT_AXIS_ORDER;
  const items: TreeItem[] = tree?.items ?? [];

  const lookups: Lookups = useMemo(() => {
    const catName = new Map<string, string>();
    for (const c of cats?.categories ?? []) catName.set(c.id, c.name);
    const subName = new Map<string, string>();
    for (const s of cats?.subcategories ?? []) subName.set(s.id, s.name);
    const subSubName = new Map<string, string>();
    for (const ss of cats?.subSubcategories ?? []) subSubName.set(ss.id, ss.name);
    return { catName, subName, subSubName };
  }, [cats]);

  const buckets = useMemo(() => buildBuckets(items, axisOrder, lookups, ""), [items, axisOrder, lookups]);

  const handleSelect = (delta: Partial<NavFilters>) => {
    patch(delta);
    const sp = new URLSearchParams();
    for (const [k, v] of Object.entries(delta)) {
      if (v !== undefined && v !== null && v !== "") sp.set(k, String(v));
    }
    navigate({ pathname: targetPath, search: sp.toString() ? `?${sp.toString()}` : "" });
  };

  if (treeLoading) {
    return <div className="text-[12px] text-ios-muted px-2 py-1">Lade Tree …</div>;
  }
  if (items.length === 0) {
    return (
      <div className="px-2 py-3 text-[12px] text-ios-muted">
        Noch keine geprüften Belege im Zeitraum — Tree zeigt sich, sobald du den ersten Beleg „Prüfung
        abschließen" klickst.
      </div>
    );
  }

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between px-2 mb-1">
        <span className="text-[10px] uppercase tracking-wider text-ios-muted font-medium">Filter</span>
        {Object.keys(filters).length > 0 && (
          <button
            type="button"
            onClick={reset}
            className="text-[11px] text-ios-blue hover:underline"
            title="Alle Filter zurücksetzen"
          >
            zurücksetzen
          </button>
        )}
      </div>
      <ul className="space-y-0.5">
        {buckets.map((b) => (
          <TreeNode
            key={b.key}
            bucket={b}
            ancestors={[]}
            depth={0}
            onSelect={handleSelect}
            activeFilters={filters}
          />
        ))}
      </ul>
      {children}
    </div>
  );
};
